// A player's Subscription tab (contracts/revenue.md §4 players, §5): the summary (status, money, customer since,
// paywall views), one card per store subscription, support grants, and the journey from install to the next
// charge as a timeline: paywall runs collapse into one step, sandbox items carry a badge and hide behind the
// Production | All switch (in the URL).

import { Link, useParams } from "react-router";
import { usePlayerRevenue } from "../../api/revenue";
import type { Grant, PlayerRevenue, Subscription } from "../../api/revenueSchemas";
import { Button, Card, Chip, EmptyState, ErrorState, Loading, PageHeader, Segmented, Stat } from "../../components/ui";
import { formatDateTime } from "../../lib/dates";
import { fmtInt, shortId } from "../../lib/format";
import {
  END_REASON_LABEL,
  fmtMoneyList,
  fmtMoneyUsd,
  fmtUsd,
  journeySteps,
  planLabel,
  productShort,
  regionName,
  statusMeta,
  STORE_LABEL,
  triggerLabel,
  type JourneyStep,
  type RevenueEnv,
} from "../../lib/revenue";
import { StripeLink } from "../payments/components";
import pages from "../pages.module.css";
import { SandboxBadge, StoreCell, SubStatusChip, useParamSetter } from "./components";
import { PlayerTabs } from "./PlayerTabs";
import r from "./revenue.module.css";

const when = (iso: string | null | undefined) => (iso ? formatDateTime(iso) : "—");

export function PlayerSubscriptionPage() {
  const { id = "" } = useParams();
  const q = usePlayerRevenue(id);
  const handle = q.data?.player.handle;
  const header = (
    <>
      <PageHeader
        title={<span className="mono">{shortId(id, 13)}</span>}
        meta={
          <>
            <Link to="/players" className={pages.back}>
              ← Players
            </Link>
            {handle ? <span className="mono">@{handle}</span> : null}
            <span className="mono">{id}</span>
          </>
        }
      />
      <PlayerTabs id={id} />
    </>
  );
  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={10} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState
          error={q.error}
          onRetry={() => void q.refetch()}
          title={[400, 404].includes((q.error as { status?: number }).status ?? 0) ? "No such player" : "Couldn't load this player's subscriptions"}
        />
      </>
    );
  return (
    <>
      {header}
      <SubscriptionView data={q.data} />
    </>
  );
}

function SubscriptionView({ data }: { data: PlayerRevenue }) {
  const [params, set] = useParamSetter();
  const production = data.subscriptions.filter((s) => s.environment === "production");
  const sandbox = data.subscriptions.length - production.length;
  // Production by default; a tester with only sandbox purchases sees them without asking.
  const raw = params.get("env");
  const env: "production" | "all" = raw === "all" || raw === "production" ? raw : production.length === 0 && sandbox > 0 ? "all" : "production";
  const shown = env === "all" ? data.subscriptions : production;
  const envOf = new Map<string, RevenueEnv>(data.subscriptions.map((s) => [s.id, s.environment]));
  const steps = journeySteps(data.journey, env, (sid) => (sid ? (envOf.get(sid) ?? null) : null));
  const { summary, player } = data;
  const country = regionName(player.country);

  return (
    <div data-testid="player-subscription">
      <Card title="Summary" actions={<SubStatusChip status={summary.status} />}>
        <div className={r.summary} data-testid="subscription-summary">
          <Stat
            label="Spent"
            value={fmtUsd(summary.spentUsd)}
            sub={summary.spent.some((m) => m.currency !== "USD") ? fmtMoneyList(summary.spent) : summary.spent.length ? "net of refunds, production" : "nothing charged in production"}
          />
          <Stat label="Customer since" value={summary.customerSince ? when(summary.customerSince) : "—"} sub={summary.customerSince ? "first store subscription" : "never bought"} />
          <Stat label="Paywall views" value={fmtInt(summary.paywallViews)} sub={summary.firstPaywallAt ? `first ${when(summary.firstPaywallAt)}` : "never shown"} />
          <Stat label="Subscriptions" value={fmtInt(production.length)} sub={sandbox ? `+ ${fmtInt(sandbox)} sandbox` : "production"} />
          <Stat label="Country" value={player.country ?? "—"} sub={[country, player.tz].filter(Boolean).join(" · ") || "not reported"} />
          <Stat label="Installed" value={when(player.createdAt)} sub={player.lastSeenAt ? `last seen ${when(player.lastSeenAt)}` : undefined} />
        </div>
      </Card>

      <div className={`${r.toolbar} ${r.afterSummary}`}>
        <Segmented
          label="Environment"
          value={env}
          options={[
            { value: "production", label: "Production" },
            { value: "all", label: "All" },
          ]}
          onChange={(v) => set({ env: v })}
        />
        {env === "production" && sandbox > 0 ? (
          <span className={r.faintLine} data-testid="sandbox-hidden">
            {fmtInt(sandbox)} sandbox {sandbox === 1 ? "subscription" : "subscriptions"} hidden.{" "}
            <Button size="sm" variant="ghost" onClick={() => set({ env: "all" })}>
              Show all
            </Button>
          </span>
        ) : null}
      </div>

      {shown.length ? (
        <div className={r.cards} data-testid="subscription-cards">
          {shown.map((s) => (
            <SubscriptionCard key={s.id} s={s} />
          ))}
        </div>
      ) : (
        <div className={r.cards}>
          <Card title="Subscriptions" className={r.wide}>
            <EmptyState title={data.subscriptions.length ? "No production subscription" : "No store subscription"}>
              {data.subscriptions.length ? "Only sandbox purchases: switch to All to see them." : "This player hasn't started a trial or bought Pro in any store."}
            </EmptyState>
          </Card>
        </div>
      )}

      <div className={r.split}>
        <Card title="Journey" actions={<span className="monoLabel">oldest first · {steps.length} steps</span>}>
          {steps.length ? <Journey steps={steps} /> : <EmptyState title="Nothing yet">No install, paywall or store event recorded.</EmptyState>}
        </Card>
        <div className={r.side}>
          <GrantsCard grants={data.grants} />
        </div>
      </div>
    </div>
  );
}

function SubscriptionCard({ s }: { s: Subscription }) {
  const m = statusMeta(s.status);
  const end = s.currentPeriodEndsAt;
  const ends =
    s.status === "lifetime"
      ? { label: "Ends", value: "never" }
      : s.status === "trial" || s.status === "active"
        ? { label: "Renews", value: when(end) }
        : s.status === "cancelled" || s.status === "trial_cancelled"
          ? { label: "Ends", value: when(end) }
          : s.status === "grace" || s.status === "billing_retry"
            ? { label: "Renewal failed", value: when(end) }
            : s.status === "expired"
              ? { label: "Ended", value: when(end) }
              : { label: m.label === "refunded" ? "Refunded" : "Revoked", value: when(s.lastEvent?.at) };
  return (
    <Card
      title={<span className={r.cardTitle}>{productShort(s.productId, s.plan)}</span>}
      actions={
        <>
          <SubStatusChip status={s.status} />
          <SandboxBadge env={s.environment} />
        </>
      }
    >
      <dl className={r.kv} data-testid="subscription-card" data-status={s.status} data-env={s.environment}>
        <dt>Plan</dt>
        <dd>
          {planLabel(s.plan)}
          <span className={r.sub}>{s.productId}</span>
        </dd>
        <dt>Store</dt>
        <dd>
          <StoreCell store={s.store} env={s.environment} />
          {s.storefront ? <span className={r.sub}>{regionName(s.storefront) ?? s.storefront}</span> : null}
        </dd>
        <dt>Started</dt>
        <dd className="mono">{when(s.startedAt)}</dd>
        <dt>Trial</dt>
        <dd>
          {s.trialEndsAt ? (
            <>
              <span className="mono">
                {Date.parse(s.trialEndsAt) > Date.now() && s.converted === null ? "ends" : "ended"} {when(s.trialEndsAt)}
              </span>
              <Chip tone={s.converted === true ? "success" : s.converted === false ? "muted" : "violet"}>
                {s.converted === true ? "converted" : s.converted === false ? "not converted" : "in trial"}
              </Chip>
            </>
          ) : (
            <span className={pages.faint}>no trial</span>
          )}
        </dd>
        <dt>{ends.label}</dt>
        <dd className="mono">
          {ends.value}
          {s.status === "expired" && s.endReason ? <span className={r.sub}>{END_REASON_LABEL[s.endReason] ?? s.endReason}</span> : null}
        </dd>
        {s.cancelledAt ? (
          <>
            <dt>Cancelled</dt>
            <dd className="mono">{when(s.cancelledAt)}</dd>
          </>
        ) : null}
        {s.graceEndsAt ? (
          <>
            <dt>Grace ends</dt>
            <dd className="mono">{when(s.graceEndsAt)}</dd>
          </>
        ) : null}
        <dt>Next charge</dt>
        <dd data-testid="next-charge">
          {s.nextCharge ? (
            <>
              <span className="mono">{fmtMoneyUsd(s.nextCharge.price, s.nextCharge.usd)}</span>
              <span className={r.sub}>{when(s.nextCharge.at)}</span>
            </>
          ) : (
            <span className={pages.faint}>none expected</span>
          )}
        </dd>
        <dt>Paid periods</dt>
        <dd className="mono">{fmtInt(s.paidPeriods)}</dd>
        <dt>Spent</dt>
        <dd>
          <span className="mono">{fmtUsd(s.spentUsd)}</span>
          {s.spent.some((x) => x.currency !== "USD") ? <span className={r.sub}>{fmtMoneyList(s.spent)}</span> : null}
        </dd>
        <dt>Paywall</dt>
        <dd>
          {s.attribution ? (
            <>
              {triggerLabel(s.attribution.trigger)}
              {s.attribution.offer ? <span className={r.sub}>offer {s.attribution.offer.replace(/_/g, " ")}</span> : null}
              {s.attribution.variant ? <span className={r.sub}>experiment variant {s.attribution.variant.toUpperCase()}</span> : null}
            </>
          ) : (
            <span className={pages.faint}>unknown</span>
          )}
        </dd>
        <dt>{s.store === "stripe" ? "Stripe id" : "Original tx"}</dt>
        <dd>
          <span className="mono">{s.externalId ?? "—"}</span>
          <StripeLink href={s.stripeUrl} />
        </dd>
      </dl>
      {m.hint ? <p className={pages.footNote}>{m.hint}</p> : null}
    </Card>
  );
}

function Journey({ steps }: { steps: JourneyStep[] }) {
  return (
    <ol className={r.timeline} data-testid="journey" aria-label="Customer journey, oldest first">
      {steps.map((s) => {
        const many = s.paywalls && s.paywalls.length > 1 ? s.paywalls : null;
        return (
          <li key={s.key} className={r.step} data-tone={s.tone} data-future={s.future || undefined} data-kind={s.kind} data-testid="journey-step">
            <span className={r.dot} aria-hidden />
            <div className={r.stepBody}>
              <div className={r.stepHead}>
                <span className={r.stepTitle}>{s.title}</span>
                {s.money ? <span className={r.stepMoney}>{s.money}</span> : null}
              </div>
              <div className={r.stepMeta}>
                <time className={r.stepTime} dateTime={s.at}>
                  {s.future ? "expected " : ""}
                  {when(s.at)}
                  {many ? ` – ${when(many[many.length - 1]!.at)}` : ""}
                </time>
                {s.note ? <span>{s.note}</span> : null}
                <SandboxBadge env={s.environment} />
              </div>
              {many ? (
                <details className={r.paywalls}>
                  <summary>Each paywall</summary>
                  <ul>
                    {many.map((p, i) => (
                      <li key={`${p.at}-${i}`}>
                        <span className={r.stepTime}>{when(p.at)}</span>
                        <span>
                          {triggerLabel(p.trigger)}
                          {p.variant ? ` · variant ${p.variant.toUpperCase()}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function GrantsCard({ grants }: { grants: Grant[] }) {
  return (
    <Card title="Support grants" actions={grants.length ? <Chip tone="violet">{fmtInt(grants.length)}</Chip> : null}>
      {grants.length === 0 ? (
        <p className={pages.faint}>No support grant. Grants (Pro for a number of days, admin only) are given on the Profile tab.</p>
      ) : (
        <ul className={r.grants} data-testid="grants">
          {grants.map((g) => {
            const live = !g.revokedAt && g.expiresAt !== null && Date.parse(g.expiresAt) > Date.now();
            return (
              <li key={g.id}>
                <span className={r.storeCell}>
                  <Chip tone={g.revokedAt ? "danger" : live ? "violet" : "muted"}>{g.revokedAt ? "revoked" : live ? "active" : "ended"}</Chip>
                  <span>{g.reason ? `“${g.reason}”` : "no reason"}</span>
                </span>
                <span className={r.sub}>
                  {g.grantedBy ?? "an admin"} · {when(g.createdAt)} → {when(g.revokedAt ?? g.expiresAt)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p className={pages.footNote}>
        Support grants aren't store purchases: they show in the journey but never in Revenue or Customers. {STORE_LABEL.appstore} and Stripe purchases
        are listed above.
      </p>
    </Card>
  );
}
